import { useRef, useState } from "react";
import { cn } from "../../../../../lib/utils";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { validateFileSizeWithToast } from "../../../../../lib/fileUploadValidation";

const SAMPLE_FILE_EXTENSIONS = ["csv", "xlsx", "xls", "xlsm"];

function getSampleFileAcceptString(): string {
  return SAMPLE_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
}

interface SampleUploadDropzoneProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
  loading?: boolean;
}

export function SampleUploadDropzone({
  onFileSelect,
  disabled = false,
  loading = false,
}: SampleUploadDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFileSizeWithToast(file) && validateFile(file)) {
        onFileSelect(file);
      }
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateFile = (file: File): boolean => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !SAMPLE_FILE_EXTENSIONS.includes(extension)) {
      toast.error(
        "Please upload a CSV or Excel file (.csv, .xlsx, .xls, .xlsm)",
      );
      return false;
    }
    return true;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !loading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || loading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFileSizeWithToast(file) && validateFile(file)) {
        onFileSelect(file);
      }
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center gap-4 transition-colors",
        isDragOver
          ? "border-stone-500 bg-stone-100"
          : "border-stone-200 bg-stone-50",
        (disabled || loading) && "opacity-50 cursor-not-allowed",
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={getSampleFileAcceptString()}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || loading}
      />

      <div className="flex flex-col items-center gap-3 text-center">
        <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
          <Upload className="w-6 h-6 text-stone-500" />
        </div>
        <div>
          <p className="text-sm font-medium text-stone-700">
            Drop your file here, or click to browse
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Supports CSV, Excel (.xlsx, .xls, .xlsm)
          </p>
        </div>
      </div>

      <Button
        variant="outline"
        onClick={handleButtonClick}
        disabled={disabled || loading}
        loading={loading}
        className="mt-2"
      >
        Select File
      </Button>
    </div>
  );
}
